import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
    Box,
    Container,
    Typography,
    Card,
    CardActionArea,
    CardContent,
    Grid,
    Chip,
    Stack,
    Alert,
    Divider,
} from "@mui/material";
import { AppModuleLayout } from "@layouts";
import { PageHead } from "@components";
import { cmsApi, CMS_USE_MOCKS } from "@api";
import { CmsPostSummary } from "@interfaces";
import { PATH_BLOG, PATH_CMS_HOME } from "@routes";

/**
 * TEMPORARY preview hub for the CMS/SEO features, mounted at `/cms`.
 *
 * This page does NOT replace any existing route — the live site keeps working
 * as-is. It's a single place to review everything the CMS layer drives
 * (backend-composed homepage, dynamic pages, blog) while the FT backend
 * endpoints are still being built and content is served from mocks. Remove this
 * route once the CMS is fully converted/live.
 */

interface LinkTile {
    title: string;
    description: string;
    to: string;
    badge?: string;
}

// Curated demo pages. There is no "list all pages" endpoint in the CMS contract
// (pages are fetched by slug), so the known demo slugs are listed explicitly.
const PAGE_TILES: LinkTile[] = [
    { title: "About Us", description: "Standard page composed from section blocks.", to: "/about-us" },
    { title: "Contact", description: "Page with a contact-style layout.", to: "/contact" },
    {
        title: "Coming Soon (draft)",
        description: "Unpublished — public view shows not-found; add ?preview=1 to preview the draft.",
        to: "/coming-soon?preview=1",
        badge: "draft",
    },
];

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 2 }}>
        {children}
    </Typography>
);

const Tile = ({ tile }: { tile: LinkTile }) => (
    <Grid item xs={12} sm={6} md={4}>
        <Card variant="outlined" sx={{ height: "100%" }}>
            <CardActionArea component={RouterLink} to={tile.to} sx={{ height: "100%", alignItems: "stretch" }}>
                <CardContent sx={{ height: "100%" }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {tile.title}
                        </Typography>
                        {tile.badge && <Chip size="small" color="warning" label={tile.badge} />}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        {tile.description}
                    </Typography>
                    <Typography variant="caption" color="primary" sx={{ display: "block", mt: 1.5 }}>
                        {tile.to}
                    </Typography>
                </CardContent>
            </CardActionArea>
        </Card>
    </Grid>
);

export const CmsShowcase = () => {
    const [posts, setPosts] = useState<CmsPostSummary[]>([]);
    const [postsState, setPostsState] = useState<"loading" | "ready" | "error">("loading");

    // Built at render time (not module scope) to avoid a circular-import TDZ:
    // Routes -> Pages -> CmsShowcase -> Routes would read these consts before init.
    const featureTiles: LinkTile[] = [
        {
            title: "Homepage (CMS hero + sections)",
            description: "Preview of the backend-composed hero and content sections around the product carousels. The live homepage still shows the original banner until the CMS is converted.",
            to: PATH_CMS_HOME,
        },
        {
            title: "Blog index",
            description: "Paginated list of published posts driven by the CMS.",
            to: PATH_BLOG,
        },
    ];

    useEffect(() => {
        document.title = "CMS Preview";
        let active = true;
        cmsApi
            .listPosts({ perPage: 12 })
            .then((res) => {
                if (!active) return;
                setPosts(res.posts || []);
                setPostsState("ready");
            })
            .catch(() => {
                if (active) setPostsState("error");
            });
        return () => {
            active = false;
        };
    }, []);

    return (
        <AppModuleLayout>
            <PageHead
                seo={{
                    title: "CMS Preview",
                    metaDescription: "Temporary preview hub for the new CMS and SEO features.",
                    noindex: true,
                    structuredDataType: "WebPage",
                }}
            />
            <Container sx={{ py: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                    CMS &amp; SEO Preview
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 720 }}>
                    A temporary hub to review all the new backend-driven content features in one place. Your
                    current live site is unchanged — this route is additive and can be removed once the CMS is
                    fully converted.
                </Typography>

                <Alert severity={CMS_USE_MOCKS ? "info" : "success"} sx={{ mt: 3 }}>
                    {CMS_USE_MOCKS
                        ? "Content mode: MOCK data. The CMS is serving built-in sample content because the backend endpoints aren't live yet. Set REACT_APP_CMS_USE_MOCKS=false to use the real backend."
                        : "Content mode: LIVE backend. The CMS is fetching real content from the FT backend endpoints."}
                </Alert>

                <SectionHeading>Core surfaces</SectionHeading>
                <Grid container spacing={2}>
                    {featureTiles.map((t) => (
                        <Tile key={t.to} tile={t} />
                    ))}
                </Grid>

                <SectionHeading>Dynamic pages</SectionHeading>
                <Grid container spacing={2}>
                    {PAGE_TILES.map((t) => (
                        <Tile key={t.to} tile={t} />
                    ))}
                </Grid>

                <SectionHeading>Blog posts</SectionHeading>
                {postsState === "loading" && (
                    <Typography variant="body2" color="text.secondary">
                        Loading posts…
                    </Typography>
                )}
                {postsState === "error" && (
                    <Alert severity="error">Could not load blog posts from the CMS.</Alert>
                )}
                {postsState === "ready" && posts.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        No published posts found.
                    </Typography>
                )}
                {postsState === "ready" && posts.length > 0 && (
                    <Grid container spacing={2}>
                        {posts.map((p) => (
                            <Tile
                                key={p.slug}
                                tile={{
                                    title: p.title,
                                    description: p.excerpt || "Blog post rendered from CMS section blocks.",
                                    to: `${PATH_BLOG}/${p.slug}`,
                                    badge: p.status === "draft" ? "draft" : undefined,
                                }}
                            />
                        ))}
                    </Grid>
                )}

                <Divider sx={{ my: 4 }} />
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        SEO / structured data
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
                        Every page above sets its own title, meta description, canonical, Open Graph tags and
                        JSON-LD structured data. Open any page and use “View Page Source” (or Google&apos;s Rich
                        Results Test) to inspect the emitted tags.
                    </Typography>
                </Box>
            </Container>
        </AppModuleLayout>
    );
};

export default CmsShowcase;
